import React, { useState } from 'react'
import { Upload, Palette, Save, Check, AlertCircle, Plus, X, Globe } from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import toast from 'react-hot-toast'
import { apiService } from '../../services/api'
import { useAuth } from '../../context/AuthContext'
import LoadingSpinner from '../common/LoadingSpinner'

const ClientBrand = () => {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [selectedColors, setSelectedColors] = useState([])
  const [logoFile, setLogoFile] = useState(null)
  const [logoPreview, setLogoPreview] = useState(null)
  const [faviconFile, setFaviconFile] = useState(null)
  const [faviconPreview, setFaviconPreview] = useState(null)
  const [customColor, setCustomColor] = useState('#000000')
  const [showCustomColorInput, setShowCustomColorInput] = useState(false)

  // Fetch client profile
  const { data: profile, isLoading } = useQuery(
    'client-profile',
    () => apiService.get('/clients/profile').then(res => res.data)
  )

  // Upload logo mutation
  const uploadLogoMutation = useMutation(
    async (formData) => {
      const response = await apiService.post('/clients/logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      return response.data
    },
    {
      onSuccess: () => {
        toast.success('Logo uploaded successfully!')
        queryClient.invalidateQueries('client-profile')
        queryClient.invalidateQueries('onboarding-checklist')
        setLogoFile(null)
        setLogoPreview(null)
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to upload logo')
      }
    }
  )

  // Upload favicon mutation
  const uploadFaviconMutation = useMutation(
    async (formData) => {
      const response = await apiService.post('/clients/favicon', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      return response.data
    },
    {
      onSuccess: () => {
        toast.success('Favicon uploaded successfully!')
        queryClient.invalidateQueries('client-profile')
        setFaviconFile(null)
        setFaviconPreview(null)
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to upload favicon')
      }
    }
  )

  // Update brand colors mutation
  const updateBrandMutation = useMutation(
    async (brandData) => {
      const response = await apiService.put('/clients/profile', brandData)
      return response.data
    },
    {
      onSuccess: () => {
        toast.success('Brand colors saved successfully!')
        queryClient.invalidateQueries('client-profile')
        queryClient.invalidateQueries('onboarding-checklist')
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to save brand colors')
      }
    }
  )

  const predefinedColors = [
    '#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6',
    '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6B7280'
  ]

  const handleLogoChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      setLogoFile(file)
      const reader = new FileReader()
      reader.onload = (e) => setLogoPreview(e.target.result)
      reader.readAsDataURL(file)
    }
  }

  const handleLogoUpload = () => {
    if (!logoFile) return
    const formData = new FormData()
    formData.append('logo', logoFile)
    uploadLogoMutation.mutate(formData)
  }

  const handleFaviconChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      setFaviconFile(file)
      const reader = new FileReader()
      reader.onload = (e) => setFaviconPreview(e.target.result)
      reader.readAsDataURL(file)
    }
  }

  const handleFaviconUpload = () => {
    if (!faviconFile) return
    const formData = new FormData()
    formData.append('favicon', faviconFile)
    uploadFaviconMutation.mutate(formData)
  }

  const handleColorToggle = (color) => {
    setSelectedColors(prev =>
      prev.includes(color)
        ? prev.filter(c => c !== color)
        : [...prev, color]
    )
  }

  const handleAddCustomColor = () => {
    if (customColor && !selectedColors.includes(customColor)) {
      setSelectedColors(prev => [...prev, customColor])
      setShowCustomColorInput(false)
      setCustomColor('#000000')
    }
  }

  const handleRemoveColor = (colorToRemove) => {
    setSelectedColors(prev => prev.filter(color => color !== colorToRemove))
  }

  const isValidHexColor = (color) => {
    return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color)
  }

  const handleSaveColors = () => {
    updateBrandMutation.mutate({ brand_colors: selectedColors })
  }

  React.useEffect(() => {
    if (profile?.brand_colors) {
      setSelectedColors(profile.brand_colors)
    }
  }, [profile])

  if (isLoading) {
    return (
      <div className="card card-pad flex items-center justify-center" style={{ minHeight: '12rem' }}>
        <LoadingSpinner size="lg" text="Loading brand settings..." />
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      {/* Header */}
      <div>
        <h1 style={{ fontSize: '1.875rem', fontWeight: 700, color: 'var(--text)', margin: 0 }}>
          Brand Settings
        </h1>
        <p className="text-soft" style={{ marginTop: '0.25rem' }}>
          Manage your brand colors and logo. Your agency uses these as a reference while designing your websites.
        </p>
      </div>

      {/* Logo Section */}
      <div className="card card-pad">
        <div className="flex items-center gap-2" style={{ marginBottom: '1.25rem' }}>
          <Upload style={{ width: '1.25rem', height: '1.25rem', color: 'var(--accent)' }} />
          <h2 className="font-semibold" style={{ fontSize: '1rem', color: 'var(--text)', margin: 0 }}>Logo</h2>
          {profile?.logo && (
            <span className="badge badge-success" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
              <Check style={{ width: '0.75rem', height: '0.75rem' }} />
              Uploaded
            </span>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem' }}>
            {/* Current Logo */}
            <div>
              <label className="font-medium" style={{ display: 'block', fontSize: '0.875rem', color: 'var(--text-soft)', marginBottom: '0.5rem' }}>
                Current Logo
              </label>
              <div style={{ width: '8rem', height: '8rem', border: '2px dashed var(--border)', borderRadius: 'var(--radius)', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--surface-2)' }}>
                {profile?.logo ? (
                  <img
                    src={profile.logo}
                    alt="Company Logo"
                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                  />
                ) : (
                  <div style={{ textAlign: 'center' }}>
                    <Upload style={{ width: '2rem', height: '2rem', color: 'var(--text-faint)', margin: '0 auto 0.5rem' }} />
                    <p className="text-muted" style={{ fontSize: '0.875rem' }}>No logo uploaded</p>
                  </div>
                )}
              </div>
            </div>

            {/* Upload New Logo */}
            <div>
              <label className="font-medium" style={{ display: 'block', fontSize: '0.875rem', color: 'var(--text-soft)', marginBottom: '0.5rem' }}>
                Upload New Logo
              </label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoChange}
                  style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}
                />

                {logoPreview && (
                  <div style={{ width: '8rem', height: '8rem', border: '2px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
                    <img
                      src={logoPreview}
                      alt="Preview"
                      style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    />
                  </div>
                )}

                <button
                  className="btn btn-primary"
                  onClick={handleLogoUpload}
                  disabled={!logoFile || uploadLogoMutation.isLoading}
                >
                  {uploadLogoMutation.isLoading ? (
                    <>
                      <LoadingSpinner size="sm" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload style={{ width: '1rem', height: '1rem' }} />
                      Upload Logo
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Favicon Section */}
      <div className="card card-pad">
        <div className="flex items-center gap-2" style={{ marginBottom: '1rem' }}>
          <Globe style={{ width: '1.25rem', height: '1.25rem', color: 'var(--accent)' }} />
          <h2 className="font-semibold" style={{ fontSize: '1rem', color: 'var(--text)', margin: 0 }}>Favicon</h2>
          {profile?.favicon && (
            <span className="badge badge-success" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
              <Check style={{ width: '0.75rem', height: '0.75rem' }} />
              Uploaded
            </span>
          )}
        </div>
        <p className="text-muted" style={{ fontSize: '0.875rem', marginBottom: '1rem' }}>
          The favicon appears in the browser tab when visitors view your website. Use a square .ico, .png, or .svg file (recommended: 32×32px).
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem' }}>
          {/* Current Favicon */}
          <div>
            <label className="font-medium" style={{ display: 'block', fontSize: '0.875rem', color: 'var(--text-soft)', marginBottom: '0.5rem' }}>
              Current Favicon
            </label>
            <div style={{ width: '5rem', height: '5rem', border: '2px dashed var(--border)', borderRadius: 'var(--radius)', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--surface-2)' }}>
              {profile?.favicon ? (
                <img
                  src={profile.favicon}
                  alt="Favicon"
                  style={{ width: '2.5rem', height: '2.5rem', objectFit: 'contain' }}
                />
              ) : (
                <div style={{ textAlign: 'center' }}>
                  <Globe style={{ width: '1.5rem', height: '1.5rem', color: 'var(--text-faint)', margin: '0 auto 0.25rem' }} />
                  <p className="text-muted" style={{ fontSize: '0.75rem' }}>None</p>
                </div>
              )}
            </div>
          </div>

          {/* Upload New Favicon */}
          <div>
            <label className="font-medium" style={{ display: 'block', fontSize: '0.875rem', color: 'var(--text-soft)', marginBottom: '0.5rem' }}>
              Upload New Favicon
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <input
                type="file"
                accept=".ico,.png,.svg,image/x-icon,image/png,image/svg+xml"
                onChange={handleFaviconChange}
                style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}
              />
              {faviconPreview && (
                <div style={{ width: '5rem', height: '5rem', border: '2px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--surface-2)' }}>
                  <img
                    src={faviconPreview}
                    alt="Preview"
                    style={{ width: '2.5rem', height: '2.5rem', objectFit: 'contain' }}
                  />
                </div>
              )}
              <button
                className="btn btn-primary"
                onClick={handleFaviconUpload}
                disabled={!faviconFile || uploadFaviconMutation.isLoading}
              >
                {uploadFaviconMutation.isLoading ? (
                  <>
                    <LoadingSpinner size="sm" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload style={{ width: '1rem', height: '1rem' }} />
                    Upload Favicon
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Brand Colors Section */}
      <div className="card card-pad">
        <div className="flex items-center gap-2" style={{ marginBottom: '1.25rem' }}>
          <Palette style={{ width: '1.25rem', height: '1.25rem', color: 'var(--accent)' }} />
          <h2 className="font-semibold" style={{ fontSize: '1rem', color: 'var(--text)', margin: 0 }}>Brand Colors</h2>
          {profile?.brand_colors?.length > 0 && (
            <span className="badge badge-success" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
              <Check style={{ width: '0.75rem', height: '0.75rem' }} />
              Set
            </span>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div>
            <label className="font-medium" style={{ display: 'block', fontSize: '0.875rem', color: 'var(--text-soft)', marginBottom: '0.75rem' }}>
              Choose Your Brand Colors
            </label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
              {predefinedColors.map((color) => (
                <button
                  key={color}
                  onClick={() => handleColorToggle(color)}
                  style={{
                    width: '3rem',
                    height: '3rem',
                    borderRadius: 'var(--radius)',
                    border: selectedColors.includes(color) ? '2px solid var(--text)' : '2px solid var(--border)',
                    backgroundColor: color,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transform: selectedColors.includes(color) ? 'scale(1.1)' : 'scale(1)',
                    transition: 'all 0.2s',
                  }}
                  title={color}
                >
                  {selectedColors.includes(color) && (
                    <Check style={{ width: '1.5rem', height: '1.5rem', color: 'white' }} />
                  )}
                </button>
              ))}

              {/* Add Custom Color Button */}
              <button
                onClick={() => setShowCustomColorInput(true)}
                style={{
                  width: '3rem',
                  height: '3rem',
                  borderRadius: 'var(--radius)',
                  border: '2px dashed var(--border)',
                  background: 'var(--surface-2)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'border-color 0.2s',
                }}
                title="Add custom color"
              >
                <Plus style={{ width: '1.5rem', height: '1.5rem', color: 'var(--text-faint)' }} />
              </button>
            </div>
          </div>

          {/* Custom Color Input */}
          {showCustomColorInput && (
            <div style={{ background: 'var(--surface-2)', borderRadius: 'var(--radius)', padding: '1rem', border: '1px solid var(--border)' }}>
              <label className="font-medium" style={{ display: 'block', fontSize: '0.875rem', color: 'var(--text-soft)', marginBottom: '0.5rem' }}>
                Add Custom Color
              </label>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={customColor}
                    onChange={(e) => setCustomColor(e.target.value)}
                    style={{ width: '3rem', height: '3rem', borderRadius: 'var(--radius)', border: '1px solid var(--border)', cursor: 'pointer' }}
                  />
                  <input
                    type="text"
                    value={customColor}
                    onChange={(e) => setCustomColor(e.target.value)}
                    placeholder="#000000"
                    className="input"
                    style={{ width: '6rem', fontFamily: 'monospace', fontSize: '0.875rem' }}
                    pattern="^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$"
                  />
                </div>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={handleAddCustomColor}
                  disabled={!isValidHexColor(customColor) || selectedColors.includes(customColor)}
                >
                  Add
                </button>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => {
                    setShowCustomColorInput(false)
                    setCustomColor('#000000')
                  }}
                >
                  Cancel
                </button>
              </div>
              {!isValidHexColor(customColor) && customColor !== '#000000' && (
                <p style={{ fontSize: '0.75rem', color: 'var(--danger)', marginTop: '0.25rem' }}>
                  Please enter a valid hex color (e.g., #FF0000 or #F00)
                </p>
              )}
              {selectedColors.includes(customColor) && isValidHexColor(customColor) && (
                <p style={{ fontSize: '0.75rem', color: 'var(--warning)', marginTop: '0.25rem' }}>
                  This color is already selected
                </p>
              )}
            </div>
          )}

          {selectedColors.length > 0 && (
            <div>
              <label className="font-medium" style={{ display: 'block', fontSize: '0.875rem', color: 'var(--text-soft)', marginBottom: '0.5rem' }}>
                Selected Colors ({selectedColors.length})
              </label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                {selectedColors.map((color) => (
                  <div key={color} className="flex items-center gap-2" style={{ padding: '0.25rem 0.75rem', background: 'var(--surface-2)', borderRadius: 'var(--radius)' }}>
                    <div
                      style={{ width: '1rem', height: '1rem', borderRadius: '50%', border: '1px solid var(--border)', backgroundColor: color, flexShrink: 0 }}
                    />
                    <span className="text-soft" style={{ fontSize: '0.875rem' }}>
                      {color}
                    </span>
                    <button
                      onClick={() => handleRemoveColor(color)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '0.125rem', borderRadius: 'var(--radius-sm)', lineHeight: 1 }}
                      title="Remove color"
                    >
                      <X style={{ width: '0.75rem', height: '0.75rem' }} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-end">
            <button
              className="btn btn-primary"
              onClick={handleSaveColors}
              disabled={selectedColors.length === 0 || updateBrandMutation.isLoading}
            >
              {updateBrandMutation.isLoading ? (
                <>
                  <LoadingSpinner size="sm" />
                  Saving...
                </>
              ) : (
                <>
                  <Save style={{ width: '1rem', height: '1rem' }} />
                  Save Colors
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ClientBrand
